/**
 * Utility for prefetching API data via service worker
 * Improves initial page load by caching data before user navigation
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Prefetch discover page data via service worker
 * Called after user authentication to warm the cache
 * @param {string} authToken - Firebase auth token
 * @param {string} username - User's username
 * @param {string} userId - User's ID
 */
export async function prefetchDiscoverData(authToken, username, userId) {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  if (!registration.active) {
    return;
  }

  // Build the discover API URL
  const discoverUrl = `${API_BASE_URL}/api/lists/discover?username=${encodeURIComponent(username)}&exclude=${encodeURIComponent(userId)}&limit=10`;

  // Send prefetch message to service worker
  registration.active.postMessage({
    type: 'PREFETCH_API',
    url: discoverUrl,
    authToken,
  });
}

/**
 * Prefetch city guides data
 * @param {string} authToken - Firebase auth token
 */
export async function prefetchCityGuides(authToken) {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  if (!registration.active) {
    return;
  }

  const cityGuidesUrl = `${API_BASE_URL}/api/city-guides`;

  registration.active.postMessage({
    type: 'PREFETCH_API',
    url: cityGuidesUrl,
    authToken,
  });
}

/**
 * Prefetch all common data after authentication
 * Call this once after user logs in to warm the cache
 * @param {object} user - User object with username and id
 * @param {string} authToken - Firebase auth token
 */
export async function prefetchCommonData(user, authToken) {
  if (!user || !authToken) {
    return;
  }

  const username = user.username;
  const userId = user.userId || user.id;

  if (!username || !userId) {
    return;
  }

  // Prefetch in parallel
  await Promise.allSettled([
    prefetchDiscoverData(authToken, username, userId),
    prefetchCityGuides(authToken),
  ]);
}
